import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import useUserStore from '@/store/user'; // 假设 Pinia store 实例会导出为 default
import { useCommonStore } from '@/store/common';
import { convivaService } from '@/services/conviva/conviva-service';
import {
  buildWatchLimitContext,
  createWatchLimitSessionId,
  isWatchingLimitEnabled,
  queryWatchLimit,
  setPendingWatchLimitContext,
  shouldBlockWatchLimitEntry,
} from '@/services/watch-limit';

/**
 * Safely load a route component with error handling.
 * Prevents Vue Router from receiving undefined components which causes
 * "Cannot use 'in' operator to search for 'displayName' in undefined" errors.
 */
function safeComponentLoader(loader: () => Promise<any>) {
  return async () => {
    try {
      const module = await loader();
      // Ensure we return a valid component (default export or the module itself)
      if (module && typeof module === 'object') {
        return module.default || module;
      }
      // If import fails or returns invalid value, redirect to home
      console.error('[Router] Component load failed, redirecting to home');
      return null; // Vue Router will handle null by not rendering
    } catch (error) {
      console.error('[Router] Error loading component:', error);
      // Return null to prevent undefined from reaching Vue Router
      return null;
    }
  };
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/search',
    name: 'Search',
    component: safeComponentLoader(() => import(/* webpackChunkName: "search" */ '@/views/search.vue')),
    meta: {
      menuBar: true,
      keepAlive: true,
    },
  },
  {
    path: '/',
    name: 'Home',
    component: safeComponentLoader(() => import(/* webpackChunkName: "home" */ '@/views/home.vue')),
    meta: {
      menuBar: true,
      keepAlive: true,
    },
  },
  {
    path: '/sports',
    name: 'Sports',
    component: safeComponentLoader(() => import(/* webpackChunkName: "home" */ '@/views/home.vue')),
    meta: {
      menuBar: true,
      keepAlive: false,
      category: 1,
    },
  },
  {
    path: '/shows',
    name: 'Shows',
    component: safeComponentLoader(() => import(/* webpackChunkName: "home" */ '@/views/home.vue')),
    meta: {
      menuBar: true,
      keepAlive: false,
      category: 2,
    },
  },
  {
    path: '/series-and-movies',
    name: 'SeriesAndMovies',
    component: safeComponentLoader(() => import(/* webpackChunkName: "home" */ '@/views/home.vue')),
    meta: {
      menuBar: true,
      keepAlive: false,
      category: 4,
    },
  },
  {
    path: '/focusTest',
    name: 'FocusTest',
    component: safeComponentLoader(() => import(/* webpackChunkName: "home" */ '@/views/focusTest.vue')),
  },
  {
    path: '/video',
    name: 'video',
    meta: {
      withoutMenuBar: true,
    },
    component: safeComponentLoader(() => import(/* webpackChunkName: "videoPage" */ '@/views/videoPage.vue')),
  },
  {
    path: '/videoTest',
    name: 'videoTest',
    meta: {
      withoutMenuBar: true,
    },
    component: safeComponentLoader(() => import(/* webpackChunkName: "videoPage" */ '@/views/videoTestPage.vue')),
  },
  {
    path: '/show/:id',
    name: 'ShowDetail',
    component: safeComponentLoader(() => import(/* webpackChunkName: "show" */ '@/views/showPage.vue')),
    meta: {
      menuBar: false,
      keepAlive: true,
    },
  },
  {
    path: '/series/:id',
    name: 'SeriesDetail',
    component: safeComponentLoader(() => import(/* webpackChunkName: "series" */ '@/views/seriesPage.vue')),
    meta: {
      menuBar: false,
      keepAlive: true,
    },
  },
  {
    path: '/movie/:id',
    name: 'MovieDetail',
    component: safeComponentLoader(() => import(/* webpackChunkName: "movie" */ '@/views/moviePage.vue')),
    meta: {
      menuBar: false,
      keepAlive: true,
    },
  },
  {
    path: '/setting',
    name: 'Setting',
    component: safeComponentLoader(() => import(/* webpackChunkName: "home" */ '@/views/settings/Index.vue')),
    meta: {
      menuBar: true,
      keepAlive: true,
    },
  },
  {
    path: '/login',
    name: 'QRLogin',
    component: safeComponentLoader(() => import(/* webpackChunkName: "home" */ '@/views/loginWithQR.vue')),
  },
  {
    path: '/offline',
    name: 'Offline',
    component: safeComponentLoader(() => import(/* webpackChunkName: "home" */ '@/views/offline.vue')),
    meta: {
      menuBar: true,
    },
  },
  {
    path: '/subscribeGuide',
    name: 'SubscribeGuide',
    component: safeComponentLoader(() => import(/* webpackChunkName: "home" */ '@/views/subscribeGuide.vue')),
  },
  {
    path: '/watch-limit',
    name: 'WatchLimit',
    meta: {
      withoutMenuBar: true,
    },
    component: safeComponentLoader(() => import(/* webpackChunkName: "watchLimit" */ '@/views/watchLimit.vue')),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'Home' },
  },
];

const router = createRouter({
  history: createWebHistory('/'),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore();
  const commonStore = useCommonStore();

  // Search results persistence: set when LEAVING Search (primary) - avoids relying on 'from' on history.back()
  // Search->Video/Show: keep results when user returns
  // Search->Home/Settings/etc: flush results when user returns
  if (from.name === 'Search') {
    commonStore.setSearchShouldKeepResults(commonStore.isAssetRoute(to));
  }
  // When arriving at Search from a main route: always flush (e.g. Search->Video->Home->Search)
  if (to.name === 'Search' && !commonStore.isAssetRoute(from)) {
    commonStore.setSearchShouldKeepResults(false);
  }
  console.log(userStore);
  if (!commonStore.isOnline && to.path !== '/offline') {
    next(`/offline?fromPage=${to.fullPath}`);
    return;
  }
  // if (to.name !== 'QRLogin' && !userStore.userInfo) {
  //   next({ name: 'QRLogin' });
  // } else if (to.name === 'QRLogin' && userStore.userInfo) {
  //   next({ name: 'Home' });
  // } else {
  //   next();
  // }

  const isPlaybackRoute =
    to.name === 'video' || to.name === 'videoTest' || (typeof to.path === 'string' && to.path.startsWith('/video'));
  if (isPlaybackRoute && !userStore.isLogin) {
    next({ name: 'QRLogin' });
    return;
  }

  if (to.name !== 'QRLogin' && userStore.guestFlag === 0 && !userStore.userInfo) {
    next({ name: 'QRLogin' });
    return;
  }

  if (isPlaybackRoute && to.name !== 'WatchLimit') {
    try {
      const watchLimitEnabled = await isWatchingLimitEnabled();
      if (watchLimitEnabled) {
        const context = await buildWatchLimitContext(to);
        if (context) {
          const result = await queryWatchLimit(context);
          if (shouldBlockWatchLimitEntry(result)) {
            setPendingWatchLimitContext({
              ...context,
              sessionId: createWatchLimitSessionId(),
              source: 'pre_navigation',
            });
            next({
              name: 'WatchLimit',
              query: {
                return: from.fullPath || '/',
              },
              replace: false,
            });
            return;
          }
        }
      }
    } catch (error) {
      console.warn('[watch-limit] Pre-navigation check failed, allowing playback navigation', error);
    }
  }

  next();
});

router.afterEach(newRoute => {
  convivaService.trackPageView(newRoute.fullPath);
});

// Handle navigation errors (including component loading failures)
router.onError(error => {
  console.error('[Router] Navigation error:', error);
  // If component loading fails, redirect to home to prevent undefined component errors
  if (error.message?.includes('displayName') || error.message?.includes('Cannot use')) {
    console.warn('[Router] Component loading error detected, this is handled gracefully');
  }
});

export default router;
